import { useState, useEffect } from 'react'
import { Zap, Clock, CheckCircle, LogIn, AlarmClock } from 'lucide-react'
import { rrGetAttendanceToday, rrPunch } from '@/actions/rr'

interface Props {
  userName: string
  onClose: () => void
}

function getGreeting() {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

export default function AttendancePunchModal({ userName, onClose }: Props) {
  const [punching, setPunching] = useState(false)
  const [done, setDone] = useState(false)
  const [punchTime, setPunchTime] = useState('')
  const [error, setError] = useState('')
  const firstName = userName.split(' ')[0]

  async function handlePunch() {
    setPunching(true)
    setError('')
    try {
      const res = await rrPunch()
      const punchData = res?.data as { action?: string; at?: string } | null
      if (punchData?.action === 'punch_in') {
        setPunchTime(punchData.at ?? '')
        setDone(true)
        setTimeout(() => {
          markShownToday()
          onClose()
        }, 4000)
      } else {
        setError('Could not punch in. Please try again.')
      }
    } catch {
      setError('Could not punch in. Please try again.')
    } finally {
      setPunching(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">

        {/* Top accent bar */}
        <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg, #157a4e, #0f9d6e)' }} />

        {!done ? (
          <>
            {/* Header */}
            <div className="px-7 pt-7 pb-5 text-center">
              <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #eafaf2, #d0f5e5)' }}>
                <AlarmClock className="w-8 h-8 text-brandColor" />
              </div>
              <h2 className="text-xl font-extrabold text-gray-900">{getGreeting()}, {firstName}!</h2>
              <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                Mark your attendance to start receiving leads.<br />
                <span className="text-xs text-gray-400">Leads are only auto-assigned to agents who have punched in.</span>
              </p>
            </div>

            {/* Time display */}
            <div className="mx-7 mb-5 flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-100 rounded-2xl">
              <div className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 text-gray-500" />
              </div>
              <div>
                <div className="text-xs text-gray-400 font-medium">Current time</div>
                <LiveClock />
              </div>
            </div>

            {/* Error */}
            {error && <p className="mx-7 mb-2 text-xs text-red-500 text-center">{error}</p>}

            {/* Actions */}
            <div className="px-7 pb-7 flex flex-col gap-2.5">
              <button
                onClick={handlePunch}
                disabled={punching}
                className="w-full h-12 bg-brandColor text-white rounded-2xl text-base font-extrabold hover:bg-brand-hover transition-colors inline-flex items-center justify-center gap-2.5 disabled:opacity-60"
              >
                {punching ? (
                  <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <LogIn className="w-5 h-5" />
                )}
                {punching ? 'Punching in…' : 'Punch In'}
              </button>
            </div>
          </>
        ) : (
          /* Success state */
          <div className="px-7 py-10 text-center">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center bg-green-50">
              <CheckCircle className="w-8 h-8 text-brandColor" />
            </div>
            <h2 className="text-xl font-extrabold text-gray-900">You're live!</h2>
            <p className="text-sm text-gray-500 mt-2">
              Punched in at <span className="font-bold text-gray-800">{punchTime}</span>.<br />
              Leads will be assigned to you shortly.
            </p>
            <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-gray-400">
              <Zap className="w-3.5 h-3.5 text-brandColor" />
              Auto-closing…
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function LiveClock() {
  const [time, setTime] = useState(new Date())
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000)
    return () => clearInterval(t)
  }, [])
  return (
    <div className="text-sm font-bold text-gray-900 tabular-nums">
      {time.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
    </div>
  )
}

// ── Helpers used by Layout ───────────────────────────────────────────────────


const SESSION_KEY = 'zh:attendance:punch_shown'

export function markShownToday() {
  sessionStorage.setItem(SESSION_KEY, '1')
}

export function wasShownToday() {
  return sessionStorage.getItem(SESSION_KEY) === '1'
}

export async function shouldShowAttendanceModal(): Promise<boolean> {
  // Already shown this session (punched in or skipped)
  if (wasShownToday()) return false
  try {
    const res = await rrGetAttendanceToday()
    const d = res?.data as { status?: string; punch_out?: string | null } | null
    // Show if absent, no record, or punched out (present but punch_out is set)
    return !d?.status || d.status === 'absent' || (d.status === 'present' && !!d.punch_out)
  } catch {
    return false
  }
}
